import { format, isDate } from 'date-fns';
import { VrboCancellationPolicy } from 'models/Channels';
import { percentageToDecimalFraction } from 'utils/numberUtils';
import {
  PropertyMainSettingsUpdatePayload,
  PropertySettingsMainTabFormDirtyFields,
  PropertySettingsMainTabFormValues,
} from './PropertySettingsMainTab.schema';
import { CHECK_IN_TIME_END_FLEXIBLE } from './sections/airbnbSettings/AirbnbSettings.constants';
import { commonFieldValueConverter } from './PropertyMainSettingsTab.utils';
import {
  AirbnbPromotionStatus,
  AirbnbPromotionType,
} from './PropertySettingsMainTab.types';
import { buildVrboPromotionsPatchPayload } from './sections/vrboSettings/PropertyVrboNewListingDiscount.utils';

const fieldValuesConverters = {
  airbnbSettings: {
    nonRefundableRateDiscount: (
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      value: PropertySettingsMainTabFormValues['airbnbSettings']['nonRefundableRateDiscount'],
    ) => {
      return typeof value === 'number'
        ? percentageToDecimalFraction(value)
        : value;
    },
  },
  bookingDotComSettings: {
    ...commonFieldValueConverter.bookingDotComSettings,
  },
  bookingSettings: {
    percentageUponReservation: (
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      value: PropertySettingsMainTabFormValues['bookingSettings']['percentageUponReservation'],
    ) => {
      return percentageToDecimalFraction(value);
    },
    daysOfTheWeekToCheckInOn: (
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      value: PropertySettingsMainTabFormValues['bookingSettings']['daysOfTheWeekToCheckInOn'],
    ) =>
      Object.entries(value)
        .filter(([_day, isChecked]) => isChecked)
        .map(([day]) => day),
  },
  miscInfo: {
    rentalLicenseExpirationDate: (
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      value: PropertySettingsMainTabFormValues['miscInfo']['rentalLicenseExpirationDate'],
    ) => (value && isDate(value) ? format(value, 'yyyy-MM-dd') : null),
  },
};

function appendMutuallyDependentFields(
  payload: PropertyMainSettingsUpdatePayload,
  formValues: PropertySettingsMainTabFormValues,
) {
  if (payload.propertyDetails?.propertySize !== undefined) {
    payload.propertyDetails.propertySizeUnit =
      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      formValues.propertyDetails.propertySizeUnit;
  } else if (payload.propertyDetails?.propertySizeUnit !== undefined) {
    payload.propertyDetails.propertySize =
      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      formValues.propertyDetails.propertySize;
  }

  if (payload.airbnbSettings?.checkInTimeStartFlexible !== undefined) {
    const { checkInTimeStartFlexible } = formValues.airbnbSettings || {};

    if (checkInTimeStartFlexible === true) {
      payload.airbnbSettings.checkInTimeStartFlexible = true;
      payload.airbnbSettings.checkInTimeEndFlexible = true;
      payload.airbnbSettings.checkInTimeEnd = null;
    }
  }

  // If `checkInTimeEnd` was changed
  if (payload.airbnbSettings?.checkInTimeEnd !== undefined) {
    // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    const { checkInTimeEnd } = formValues.airbnbSettings;

    if (checkInTimeEnd === CHECK_IN_TIME_END_FLEXIBLE) {
      payload.airbnbSettings.checkInTimeEnd = null;
      payload.airbnbSettings.checkInTimeEndFlexible = true;
    } else {
      const checkInTimeEndParsed = Number.parseInt(checkInTimeEnd, 10);
      payload.airbnbSettings.checkInTimeEnd = checkInTimeEndParsed;
      payload.airbnbSettings.checkInTimeEndFlexible = false;
    }
  }

  // In the case the Vrbo cancellation policy is no longer "CUSTOM" but the `customCancellationPolicies` array is still
  // present, we need to send it as `null` in the payload
  if (
    payload.vrboSettings?.cancellationPolicy &&
    payload.vrboSettings.cancellationPolicy !== VrboCancellationPolicy.CUSTOM &&
    formValues.vrboSettings?.customCancellationPolicies?.length
  ) {
    payload.vrboSettings.customCancellationPolicies = null;
  }

  return payload;
}

const buildAirbnbPromotionsPatchPayload = (
  formValues: PropertySettingsMainTabFormValues,
) => {
  const formStatus = formValues.airbnbSettings?.promotions?.[0]?.status;
  // BE reads EXPIRED as the dis-enroll instruction.
  const status =
    formStatus === AirbnbPromotionStatus.AVAILABLE
      ? AirbnbPromotionStatus.EXPIRED
      : formStatus;

  return [{ type: AirbnbPromotionType.NEW_LISTING_PROMOTION, status }];
};

const usePropertyMainSettingsUpdatePayload = () => {
  return ({
    dirtyFields,
    formValues,
  }: {
    dirtyFields: PropertySettingsMainTabFormDirtyFields;
    formValues: PropertySettingsMainTabFormValues;
  }) => {
    const payload: PropertyMainSettingsUpdatePayload = Object.entries(
      dirtyFields,
    ).reduce((acc, [sectionName, sectionDirtyFields]) => {
      return {
        ...acc,
        [sectionName]: Object.entries(sectionDirtyFields)
          .filter(([_, isDirty]) => isDirty)
          .reduce((sectionAcc, [fieldName, isDirtyOrDirtyObject]) => {
            // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            const fieldValue = formValues[sectionName][fieldName];
            const fieldValueConverter =
              // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              fieldValuesConverters[sectionName]?.[fieldName];

            return {
              ...sectionAcc,
              [fieldName]: fieldValueConverter
                ? fieldValueConverter(fieldValue, isDirtyOrDirtyObject)
                : fieldValue,
            };
          }, {}),
      };
    }, {});

    if (payload.propertyNotes?.notes_htmlContent) {
      payload.propertyNotes = {
        notes: payload.propertyNotes?.notes_htmlContent,
      };
    }

    const shouldIncludePromotions =
      dirtyFields.vrboSettings?.promotions ||
      dirtyFields.vrboSettings?.newListingDiscount;

    if (shouldIncludePromotions) {
      payload.vrboSettings = {
        ...payload.vrboSettings,
        promotions: buildVrboPromotionsPatchPayload(formValues),
      };

      if (
        payload.vrboSettings &&
        'newListingDiscount' in payload.vrboSettings
      ) {
        delete payload.vrboSettings.newListingDiscount;
      }
    }

    if (dirtyFields.airbnbSettings?.promotions) {
      payload.airbnbSettings = {
        ...payload.airbnbSettings,
        promotions: buildAirbnbPromotionsPatchPayload(formValues),
      };
    }

    appendMutuallyDependentFields(payload, formValues);

    return payload;
  };
};

export default usePropertyMainSettingsUpdatePayload;
